import * as React from 'react';
import { GeneralSettingsContext } from '@app/Settings/General/GeneralSettings';

// Miscellaneous Patternfly components.
import {
  PageSection,
  Bullseye,
  Card,
  CardHeader,
  CardBody,
  EmptyState,
  EmptyStateBody,
  Flex,
  Split,
  Title,
  Spinner,
  SplitItem,
  Content,
  } from '@patternfly/react-core';

// To retrieve the parameter from the URL.
import { useParams } from 'react-router-dom';

// This is to compress all warnings about mismatched types when accessing the resourceData object.
import type ResourceTypes from '@app/Resources/ResourceTypes'

// For the "no resource found" error.
import CubesIcon from '@patternfly/react-icons/dist/esm/icons/cubes-icon';

// For the "back" button to navigate back to the "All Resources" page.
import AngleLeftIcon from '@patternfly/react-icons/dist/esm/icons/angle-left-icon';

// For the donut charts.
import {
	ChartDonutUtilization,
	ChartPie,
	ChartLegend,
	ChartThemeColor,
	ChartTooltip
} from '@patternfly/react-charts';

// For the expandable section, which has all of the experiments in a list.
import { ExpandableSection, ExpandableSectionVariant, Truncate } from '@patternfly/react-core';

// For the API calls.
import { useFetch } from 'use-http';
import { format } from 'd3';

import './DetailedResources.css';


const DetailedResources: React.FunctionComponent = () => {
    // Some hooks.
    const [loaded, setLoaded] = React.useState(false);

    // Assigning this of type ResourceTypes (custom file) to hide all the warnings when accessing the object.
    const [resourceData, setResourceData] = React.useState<ResourceTypes | null>(null);

    // This is for storing the allocated experiments.
    const [experimentData, setExperimentData] = React.useState<string[] | null>(null);

    // For the expandable section.
    const [isExpanded, setIsExpanded] = React.useState(false);

    const onToggle = (_event: React.MouseEvent, isExpanded: boolean) => {
        setIsExpanded(isExpanded);
    };

    // Similar to the Resources.tsx file, creating a hook for used/all cores and memory.
    const [resourceInfo, setResourceInfo] = React.useState<{
        usedCores: number,
        usedMemory: number,
        totalCores: number,
        totalMemory: number
    }>();

    // Obtaining the resource ID.
    const { resourceid } = useParams<{ resourceid: string }>();

    // Checking to see if the requested resource exists.
    const conf = React.useContext(GeneralSettingsContext);
    const options = { credentials: 'include', cachePolicy: 'no-cache' };
    const {
        loading: resourcesLoading,
        error: resourcesError,
        get: getResources,
        response: resourcesResponse,
    } = useFetch(conf.api + '/realize/resources', options, []);

    // Getting the requested resource in the URL.
    const fetchResource = React.useCallback(async () => {
        try {
            await getResources();
            if (resourcesResponse.ok) {
                const json = await resourcesResponse.json();

                // Resources have been obtained. Locate the specific resource that's been requested.
                (json?.resources).forEach((item) => {
                    if (item?.resource?.id === resourceid) {
                        setExperimentData(item);
                        setResourceData(item?.resource);
                        return;
                    }
                });
                
                setLoaded(true);
            }
        }
        
        catch (error) {
            console.log('Error fetching users:', error);
        }
    }, [getResources, resourcesResponse]);

    React.useEffect(() => {
        if (experimentData && resourceData) {
            let usedCores = 0;
            let usedMemory = 0;

            experimentData.allocated.forEach((exp: any) => {
                usedCores += exp?.coresUsed;
                usedMemory += Number(exp?.memoryUsed);
            });

            const totalCores = resourceData?.procs.reduce((total: number, processor: { cores: string }) => {
                return total + Number(processor.cores);
            }, 0);

            const totalMemory = resourceData?.memory.reduce((total: number, block: { capacity: string }) => {
                return total + Number(block.capacity);
            }, 0);

            setResourceInfo({
                usedCores,
                usedMemory,
                totalCores,
                totalMemory
            });
        }
    }, [experimentData, resourceData]);


    React.useEffect(() => {
        fetchResource();
    }, []);

    const hardwareInfo = () => {
        const components: JSX.Element[] = [];

        const processor = resourceData?.procs?.[0];
        const memory = resourceData?.memory || [];
        const nics = resourceData?.NICs || [];
        const disk = resourceData?.disks || [];
        const productInfo = resourceData?.productInfo;
        const os = resourceData?.OS;

        // Creating an OS/Boot card.
        if (os) {
            components.push(
            <Card className="hardware-card">
                <CardHeader className="card-header-bold">Operating System</CardHeader>
                <CardBody>
                    <div><strong>Default Image:</strong> {`${os.defaultImage}`}</div>
                    <div><strong>Boot Device:</strong> {`${os.rootdev}`}</div>
                </CardBody>
            </Card>
            );
        }

        // Creating a processor card.
        if (processor) {
            components.push(
            <Card className="hardware-card">
                <CardHeader className="card-header-bold">Processor</CardHeader>
                <CardBody>
                    <div><strong>Name: </strong> 
                    {`${processor.productInfo?.Manufacturer === undefined ? "" : processor.productInfo?.Manufacturer} 
                    ${processor.productInfo?.Model === undefined ? "Unknown" : processor.productInfo?.Model}`}
                    </div>
                    <div><strong>Cores:</strong> {`${processor.cores === undefined ? "Unknown" : processor.cores}`}</div>
                    <div><strong>Threads:</strong> {`${processor.threads || processor.cores}`}</div>
                    <div><strong>Hyperthreaded:</strong> {`${processor.hyperthreaded ? "Yes" : "No"}`}</div>
                    <div><strong>Instruction Set:</strong> {`${processor.isa === undefined ? "Unknown" : processor.isa}`}</div>
                    <div><strong>SKU:</strong> {`${processor.productInfo?.SKU === undefined ? "Unknown" : processor.productInfo?.SKU}`}</div>
                </CardBody>
            </Card>
            );
        }

        // Creating a memory card.
        if (memory.length > 0) {
            const moduleCapacityGB = parseInt(memory[0].capacity) / (1024 ** 3);
            const totalCapacityGB = moduleCapacityGB * memory.length;
            components.push(
            <Card className="hardware-card">
                <CardHeader className="card-header-bold">Memory</CardHeader>
                <CardBody>
                    <div><strong>Type:</strong> {`${memory[0].type}`}</div>
                    <div><strong>Module Size:</strong> {`${moduleCapacityGB} GB`}</div>
                    <div><strong>Module Count:</strong> {`${memory.length}`}</div>
                    <div><strong>Manufacturer:</strong> {`${memory[0].productInfo?.Manufacturer === undefined ? "Unknown" : memory[0].productInfo?.Manufacturer}`}</div>
                    <div><strong>Module:</strong> {`${memory[0].productInfo?.Model === undefined ? "Unknown" : memory[0].productInfo?.Model}`}</div>
                    <br></br><div><strong>Total Memory:</strong> {`${totalCapacityGB} GB`}</div>
                </CardBody>
            </Card>
            );
        }

        // Creating a disk card.
        if (disk) {
            // In GB. Convert towards the end. Using GB to prevent integer overflow when converting to bytes.
            let totalCapacity = 0.0;

            type Drive = {
                driveName: string;
                capacity: number;
                quantity: number;
            };

            let drives: Drive[] = [];

            // Group 0: Amount of storage.
            // Group 1: Storage units.
            const storageCapture = /(\d+(?:\.\d+)?)(?:\s?)(GB|TB|PB)/;

            // Iterate through them and create a new object out of them.
            disk.forEach((drive) => {
                // Get the full name of the drive.
                let fullName = `${drive?.productInfo?.Manufacturer} ${drive?.productInfo?.Model}`;
                let diskCapacity = 0;

                // Everything in this code block is if you want to rely on the storage displayed in the name of the disk.
                // If the disk does NOT contain a storage amount, it will use the API's listed capacity as a fallback.
                // There's a discrepancy between the storage on the disk's name, versus the actual capacity. Uncomment
                // this section if you wish to rely on what the disk is named!

                //////////////////////////
                // const match = drive?.productInfo?.Model.match(storageCapture);

                // if (match) {
                //     let [, size, unit] = match;
                //     size = parseFloat(size);
                //     diskCapacity = unit === "TB" ? size *= 1000 : size;
                //     diskCapacity = unit === "PB" ? size *= 1e+6 : size;
                //     totalCapacity += diskCapacity;
                // } 
                
                // // Should there be no match, then rely on the capacity from the API.
                // else {
                //     diskCapacity = (drive?.capacity) / 1e+9;
                //     totalCapacity += diskCapacity;
                // }
                //////////////////////////

                diskCapacity = (drive?.capacity) / 1e+9;
                totalCapacity += diskCapacity;

                let exists = drives.find(e => e.driveName === fullName);
                if (exists) {
                    exists.quantity += 1;
                    exists.capacity += diskCapacity;
                }

                else {
                    drives.push({
                        driveName: fullName,
                        capacity: (drive?.capacity) / 1e+9, // Replace with "capacity: diskCapacity" if uncommenting what's above.
                        quantity: 1
                    });
                }
            });

            components.push(
            <Card className="hardware-card">
                <CardHeader className="card-header-bold">Disks</CardHeader>
                <CardBody>
                    {drives.map((drive, i) => (
                    <div key={`drive-${i}`} className="drive-item">
                        <div><strong>Name:</strong> {`${drive.driveName}`}</div>
                        <div><strong>Quantity:</strong> {`${drive.quantity}`}</div>
                    </div>
                    ))}

                    <div>
                        <strong>Total Storage:</strong> {`${totalCapacity >= 1000 ? (totalCapacity / 1000).toFixed(2) + " TB" : (totalCapacity).toFixed(2) + " GB"}`}
                    </div>
                </CardBody>
            </Card>
            );
        }

        // There may be a GPU available, but it's listed under NICs.
        if (nics.length > 0) {
            // Loop through the NICs to see if there's one with a GPU in its name.
            nics.forEach((nic) => {
                if (
                    nic?.productInfo &&
                    typeof nic.productInfo.Model === "string" &&
                    (nic.productInfo.Model.includes("GPU") || nic.productInfo.Model.includes("gpu"))
                ) {
                    components.push(
                    <Card className="hardware-card">
                        <CardHeader className="card-header-bold">Graphics Card</CardHeader>
                        <CardBody>
                            <div className="nic-item">
                                <div><strong>Kind:</strong> {`${nic.kind}`}</div>
                                {nic?.productInfo && (
                                <>
                                    <div><strong>Manufacturer:</strong> {`${nic?.productInfo?.Manufacturer}`}</div>
                                    <div><strong>Model:</strong> {`${nic?.productInfo?.Model}`}</div>
                                    <div><strong>Integrated:</strong> {`${nic?.productInfo?.Integrated ? "Yes" : "No"
                                    }`}</div>
                                </>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                    );
                    return;
                }
            });
            
        }

        // Creating a NICs card.
        if (nics.length > 0) {
            components.push(
            <Card className="hardware-card">
                <CardHeader className="card-header-bold">Network Interfaces</CardHeader>
                <CardBody>
                    {nics.map((nic, i) => (
                    <div key={`nic-${i}`} className="nic-item">
                        <div><strong>Kind:</strong> {`${nic.kind}`}</div>
                        {nic.productInfo && (
                        <>
                            <div><strong>Manufacturer:</strong> {`${nic.productInfo.Manufacturer}`}</div>
                            <div><strong>Model:</strong> {`${nic.productInfo.Model}`}</div>
                        </>
                        )}
                        <div><strong>Ports:</strong> {`${nic.ports.length}`}</div>
                        {nic.ports.map((port, j) => {
                        const gbps = port.capacity ? parseInt(port.capacity) / 1e9 : 0;
                        return (
                            <div key={`port-${j}`}>
                            <strong>Port:</strong> {`${port.name || "Unnamed"} (${gbps} Gbps)`}
                            </div>
                        );
                        })}
                    </div>
                    ))}
                </CardBody>
            </Card>
            );
        }

        // Creating a system info card.
        if (productInfo) {
            components.push(
            <Card className="hardware-card">
                <CardHeader className="card-header-bold">System Info</CardHeader>
                <CardBody>
                    <div><strong>Manufacturer:</strong> {`${productInfo.Manufacturer}`}</div>
                    <div><strong>Model:</strong> {`${productInfo.Model}`}</div>
                    <div><strong>SKU:</strong> {`${productInfo.SKU}`}</div>
                    <div><strong>Roles:</strong> {`${[...new Set(resourceData.roles)].join(", ")}`}</div>
                    <div><strong>Allocation:</strong> {`${resourceData.alloc?.join(", ")}`}</div>
                </CardBody>
            </Card>
            );
        }

        return (
        <>
            <Flex wrap={{ default: 'wrap' }} spaceItems={{ default: 'spaceItemsMd' }}>
                {components}
            </Flex>
        </>
        );
    };

    // Displaying the entire "detailed resource" page here.
    const header = (
        <PageSection hasBodyWrapper={false}>
        <Split>
            <SplitItem>
            <Title headingLevel="h1" size="lg">
                Detailed Resources
            </Title>
            </SplitItem>
        </Split>
        </PageSection>
    );

    const resourceGraphs = () => {
        // Do not show blank graphs until loaded.
        if (!loaded) { return (<></>) }

        // If undefined, set total cores/memory to very low value (not zero) to avoid division by zero.
        let usedCores = resourceInfo?.usedCores || 0;
        let totalCores = resourceInfo?.totalCores || 1.0e-7;
        let usedMemory = resourceInfo?.usedMemory || 0;
        let totalMemory = resourceInfo?.totalMemory || 1.0e-7;

        let percentageCoresUsed = parseFloat(((usedCores / totalCores) * 100).toFixed(2));
        let percentageCoresAvail = (100 - percentageCoresUsed).toFixed(2);
        let percentageMemUsed = parseFloat(((usedMemory / totalMemory) * 100).toFixed(2));
        let percentageMemAvail = (100 - percentageMemUsed).toFixed(2);

        // We are going to get the number of nodes, cores, and memory for each experiment.
        interface ExpOverview {
            expName: string,
            nodes: number,
            cores: number,
            memory: number
        };

        let expOverview: ExpOverview[] = [];

        (experimentData?.allocated).forEach(exp => {
            // Defining the experiment name.
            const expName = `${exp?.rid}.${exp?.eid}.${exp?.pid}`;
            // Finding it in the array.
            const expToUpdate = expOverview.find(expItem => expItem.expName === expName);

            // Checking to see if it exists.
            if (!expToUpdate) {
                // If not, add it.
                expOverview.push({
                    expName: expName,
                    nodes: 1,
                    cores: exp.coresUsed,
                    memory: Number(exp.memoryUsed)
                });
            }

            // Otherwise, it already exists, and we just found another one of its nodes. Just update it.
            else {
                expToUpdate?.nodes += 1;
                expToUpdate?.cores += exp.coresUsed;
                expToUpdate?.memory += Number(exp.memoryUsed);
            }
        });

        // Sort alphabetically.
        if (expOverview.length > 1) {
            expOverview.sort((a, b) => {
                if (b.expName[0] > a.expName[0]) return -1;
                if (b.expName[0] < a.expName[0]) return 1;
                return 0;
            });
        }
        
        const content = (expOverview ?? []).map((exp, index) => {
            // Create a Text attribute with all of the info. Add a divider at the top if it's the first element.
            // This is to make everything appear nicer.
            return (
                <>
                    {index === 0 ? <hr className="experiment-divider"/> : ""}
                    <Content component="p" className="experiment-name" style={{ marginBottom: '0px' }}>
                        <strong>{`${exp?.expName}`}</strong>
                    </Content>
                    <Content component="p" className="experiment-details" style={{ marginBottom: '0px' }}>
                        {`Nodes: ${exp?.nodes}, Cores: ${exp?.cores}, Memory: ${formatBytes(exp?.memory)}`}
                    </Content>
                    <hr className="experiment-divider"/>
                </>
            );
        });

        return (
            <div className="resource-graphs-container">
                {/* Cores Used */}
                <div className="chart-container">
                    <Content component="p" className="chart-title">Cores Used</Content>
                    <ChartDonutUtilization
                        ariaDesc="Cores Available"
                        ariaTitle={`Cores Available to ${resourceid}`}
                        constrainToVisibleArea
                        data={{ x: 'Used Cores', y: percentageCoresUsed }}
                        labels={({ datum }) => datum.x ? `Total Usage: ${percentageCoresUsed}%` : null}
                        legendData={[{ name: `Used: ${percentageCoresUsed}%` }, { name: `Available: ${percentageCoresAvail}%` }]}
                        legendOrientation="horizontal"
                        legendPosition="bottom"
                        name="coreChart"
                        padding={{
                            bottom: 90,
                        }}
                        subTitle={`of ${totalCores} cores`}
                        title={`${usedCores}`}
                        thresholds={[{ value: 60 }, { value: 90 }]}
                        width={435}
                    />
                </div>
                
                {/* Memory Used */}
                <div className="chart-container">
                    <Content component="p" className="chart-title">Memory Used</Content>
                    <ChartDonutUtilization
                        ariaDesc="Memory Available"
                        ariaTitle={`Memory Available to ${resourceid}`}
                        constrainToVisibleArea
                        data={{ x: 'Used Memory', y: percentageMemUsed }}
                        labels={({ datum }) => datum.x ? `Total Usage: ${(formatBytes(percentageMemUsed))}%` : null}
                        legendData={[{ name: `Used: ${percentageMemUsed}%` }, { name: `Available: ${percentageMemAvail}%` }]}
                        legendOrientation="horizontal"
                        legendPosition="bottom"
                        name="memoryChart"
                        padding={{
                            bottom: 90,
                        }}
                        subTitle={`of ${formatBytes(totalMemory)}`}
                        title={`${formatBytes(usedMemory)}`}
                        thresholds={[{ value: 60 }, { value: 90 }]}
                        width={435}
                    />
                </div>
                
                {/* All Experiments Section */}
                <div className="experiments-container">
                    <Content component="p" className="chart-title">All Experiments</Content>
                    
                    {content.length === 0 
                    ? (<Content component="p" className="no-experiments-text">No experiments are on this resource.</Content>) : 
                    content.length > 0 && content.length < 3 ? <div className="experiments-center">{content}</div> : (
                        <ExpandableSection
                            variant={ExpandableSectionVariant.truncate}
                            toggleText={isExpanded ? 'Show fewer experiments' : 'Show all experiments'}
                            onToggle={onToggle}
                            isExpanded={isExpanded}
                            truncateMaxLines={-1}
                            className="experiments-expandable"
                        >
                            {isExpanded ? (
                                content
                            ) : (
                                // Using webkit-mask-image to overlay a transparent gradient over the text as it gets
                                // closer to the "show all experiments" button.
                                // Reference: https://www.w3schools.com/css/css3_masking.asp
                                <div className="experiments-truncated">
                                    <Truncate content={content} />
                                </div>
                            )}
                        </ExpandableSection>
                    )}
                    
                </div>
            </div>

        );
    };

    // Page layout.
    const loadPage = () => {
        // If it's loading... 
        if (!loaded) {
            return (
            <Bullseye className="loading-spinner">
                <Spinner />
            </Bullseye>
            );
        }

        // Requested resource doesn't exist.
        else if (!resourceData) {
            return (
            <CardBody>
                <EmptyState titleText={<Title headingLevel="h4" size="lg">
                    {resourceid} was not found.
                    </Title>} icon={CubesIcon}>
                    <EmptyStateBody>
                        This resource is unavailable. Check the <a href="resources">Resources</a> page for a list of available resources.
                    </EmptyStateBody>
                </EmptyState>
            </CardBody>
            );
        }

        else {
            return (
            <CardBody>
                {resourceGraphs()}
                {topExperiments()}
                {hardwareInfo()}
            </CardBody>
            );
        }
    };

    const topExperiments = () => {
        // Do not show blank graphs until loaded.
        if (!loaded) { return (<></>) }

        // Get the total capacity of the cores/memory used.
        interface ResourceData {
            expName: string;
            totalAmount: number;
        }

        let topCores: ResourceData[] = [];
        let topMemory: ResourceData[] = [];

        experimentData?.allocated.forEach((exp: any) => {
            // Naming the experiment.
            let name = `${exp?.rid}.${exp?.eid}.${exp?.pid}`;

            // Adding to the used cores/memory.
            let usedCores = resourceInfo?.usedCores;
            let usedMem = resourceInfo?.usedMem;

            // Pushing everything to top cores/memory. This will be sorted/trimmed after the loop.
            topCores.push({expName: name, totalAmount: exp?.coresUsed});
            topMemory.push({expName: name, totalAmount: exp?.memoryUsed});
        });

        // Sorting in descending order so that we can get the top ten.
        topCores.sort((a, b) => b.totalAmount - a.totalAmount);
        topMemory.sort((a, b) => b.totalAmount - a.totalAmount);

        topCores = topCores.slice(0, 10);
        topMemory = topMemory.slice(0, 10);

        // In order to use this with the pie chart, we must tally these up to 100. We will calculate the percentages
        // of these, then use them as the data.
        const topCoreTotal = topCores.reduce((total: number, cores: {expName: string, totalAmount: number}) => {
            return total + cores.totalAmount;
        }, 0);

        const topMemoryTotal = topMemory.reduce((total: number, memory: {expName: string, totalAmount: number}) => {
            return total + Number(memory.totalAmount);
        }, 0);

        // Converting them into percentages now.
        const coresWithPercentages = topCores.map(item => ({
            ...item,
            percentage: parseFloat(((item.totalAmount / topCoreTotal) * 100).toFixed(2))
        }));

        const memoryWithPercentages = topMemory.map(item => ({
            ...item,
            percentage: parseFloat(((item.totalAmount / topMemoryTotal) * 100).toFixed(2))
        }));
        
        // Now, we will turn these into x/y maps for the ChartPies.
        const corePieData = coresWithPercentages.map(item => ({
            x: item.expName,
            y: item.percentage
        }));

        const memoryPieData = memoryWithPercentages.map(item => ({
            x: item.expName,
            y: item.percentage
        }));

        // Creating legends based off of the total cores/memory. Not the percentages.
        const coreLegendData = topCores.map(item => {
            const unit = item.totalAmount === 1 ? " core" : " cores";
            return {
                name: item.expName + ": " + item.totalAmount + unit
            };
        });

        const memoryLegendData = topMemory.map(item => ({ name: item.expName + ": " + formatBytes(item.totalAmount) }));

        // Check to see if any experiments are available before showing these graphs.
        if (corePieData.length === 0 && memoryPieData.length === 0) {
            return ("")
        }

        else {
            return (
                <>
                <Flex 
                    wrap={{ default: 'wrap' }} 
                    spaceItems={{ default: 'spaceItemsMd' }} 
                    className="top-experiments-flex"
                >
                    {/* Top 10 Most Cores In Use */}
                    <Card className="pie-chart-card">
                        <CardHeader className="card-header-bold">Most Cores In Use</CardHeader>
                        <CardBody className="pie-chart-body">
                            <ChartPie
                            ariaDesc="Top 10 Cores In Use"
                            ariaTitle="Cores In Use"
                            data={corePieData}
                            labels={({ datum }) => `${datum.x}`}
                            labelComponent={
                                <ChartTooltip 
                                flyoutStyle={{ 
                                    fillOpacity: 0.95,
                                    padding: 6,
                                    fontSize: 6
                                }}
                                style={{ fontSize: 7 }}
                                constrainToVisibleArea={true}
                                />
                            }
                            legendComponent={
                                <ChartLegend
                                gutter={10}
                                style={{
                                    labels: { fontSize: 7 },
                                    data: { symbol: { size: 8 } }
                                }}
                                />
                            }
                            legendData={coreLegendData}
                            legendOrientation="vertical"
                            legendPosition="bottom"
                            name="coreConsumption"
                            padding={{
                                bottom: 150,
                                left: 20,
                                right: 20,
                                top: 20
                            }}
                            themeColor={ChartThemeColor.multiOrdered}
                            width={300}
                            height={250}
                            />
                        </CardBody>
                    </Card>

                    {/* Top 10 Most Memory In Use */}
                    <Card className="pie-chart-card">
                        <CardHeader className="card-header-bold">Most Memory In Use</CardHeader>
                        <CardBody className="pie-chart-body">
                            <ChartPie
                            ariaDesc="Top 10 Memory In Use"
                            ariaTitle="Memory In Use"
                            data={memoryPieData}
                            labels={({ datum }) => `${datum.x}`}
                            labelComponent={
                                <ChartTooltip 
                                flyoutStyle={{ 
                                    fillOpacity: 0.95,
                                    padding: 6,
                                    fontSize: 6
                                }}
                                style={{ fontSize: 7 }}
                                constrainToVisibleArea={true}
                                />
                            }
                            legendComponent={
                                <ChartLegend
                                gutter={10}
                                style={{
                                    labels: { fontSize: 7 },
                                    data: { symbol: { size: 8 } }
                                }}
                                />
                            }
                            legendData={memoryLegendData}
                            legendOrientation="vertical"
                            legendPosition="bottom"
                            name="memoryConsumption"
                            padding={{
                                bottom: 150,
                                left: 20,
                                right: 20,
                                top: 20
                            }}
                            themeColor={ChartThemeColor.multiOrdered}
                            width={300}
                            height={250}
                            />
                        </CardBody>
                    </Card>
                </Flex>
                </>
            )
        }
        
    }

    return (
        <React.Fragment>
            {/* Creating the header. */}
            {header}
            <PageSection hasBodyWrapper={false}>
                <Card>
                    <CardHeader className="back-link-header"><a href="resources/"><AngleLeftIcon />All Resources</a></CardHeader>
                    <CardHeader className="card-header-bold">Resource Overview for {resourceid}</CardHeader>
                    {loadPage()}
                </Card>
            </PageSection>
        </React.Fragment>
        );
}

// To format memory.
function formatBytes(bytes: number, decimals = 0) {
  if (!+bytes) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Rounding the decimal for any units more than TB.
  const dm = i >= 4 ? 2 : decimals;

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export { DetailedResources };